import { buildCmmdRuntimeEnvironmentId } from "./environments/cmmdRuntimeEnvironmentId";
import { canonicalRepoKeyOf } from "./projectGroupRoute";

/** The catalog fields a cloud project needs to be addressable as a checkout. */
export interface CloudCatalogRow {
  readonly runtimeId: string | null;
  readonly cmmdProjectId: number;
  readonly projectName: string;
  readonly githubRepoFullName: string | null;
  readonly workspaceRoot: string | null;
}

/** A cloud project, shaped like the group members the settings page reads. */
export interface CloudGroupMember {
  readonly environmentId: string;
  readonly id: string;
  readonly name: string;
  readonly cwd: string;
  readonly workspaceRoot: string;
  /**
   * What to show where a connection name goes.
   *
   * Without this the switcher fell back to "This machine" for a sprite — wrong
   * about where it is, and identical for two sprites of one repo, which share
   * a workspace root. The CMMD project name is what actually tells them apart.
   */
  readonly connectionLabel: string;
  readonly cmmdProjectId: number;
  /**
   * Always null: the CMMD catalog stores no model default. The sprite's own
   * project row holds one, but only once that runtime is connected — so on a
   * cloud-only group the page shows the connection's default and says so,
   * rather than inventing a stored value it cannot read.
   */
  readonly defaultModelSelection: null;
  /**
   * Always null, for the same reason as the model above: the CMMD catalog
   * stores no per-project workspace pin. The sprite's own project row may hold
   * one, but only once that runtime is connected -- so a cloud-only group shows
   * the app setting and says it is inherited, rather than inventing a pin it
   * cannot read.
   */
  readonly defaultThreadEnvMode: null;
}

/**
 * The cloud projects that belong to this repository group.
 *
 * Decision 3 of the repo-first model — "sprites join the group and inherit its
 * defaults. That is the point." A cloud catalog row already knows its
 * repository and its path, but the sidebar snapshot parks both inside
 * `preConnectCloud` and sets `repositoryIdentity: null` and `cwd: ""`, so
 * `deriveLogicalProjectKey` falls back and the sprite never joins its group.
 *
 * The visible cost: standing on forge-cloud — which IS a connection — the group
 * settings page reported "No checkout on this machine" for a project whose
 * sprite was registered, and invited you to open the repository "from a
 * connection".
 *
 * Resolved here rather than by re-keying the sidebar row. `deriveCloudProjectKey`
 * prefixes `cloud:` deliberately so cloud and local keys stay provably disjoint
 * for expansion state, DnD ids and thread routing; that invariant is
 * load-bearing and this does not disturb it.
 */
export function resolveCloudGroupMembers(
  groupKey: string,
  rows: readonly CloudCatalogRow[],
): readonly CloudGroupMember[] {
  return rows.flatMap((row) => {
    // No runtime means no environment to address, so it cannot be a checkout
    // yet — including it would add a member nothing can read from or write to.
    if (!row.runtimeId) return [];

    if (repoKeyOfCatalogRow(row.githubRepoFullName) !== groupKey) return [];

    const workspaceRoot = row.workspaceRoot ?? "";
    return [
      {
        // Built by the shared module rather than re-derived: a second copy of
        // the `cmmd-runtime:` format is what its guard test exists to stop.
        environmentId: buildCmmdRuntimeEnvironmentId(row.runtimeId),
        // Distinct per row: the settings page keys checkouts by
        // `${environmentId} ${id}`, so two rows collapsing onto one id would
        // render a single checkout for two sprites.
        id: String(row.cmmdProjectId),
        name: row.projectName,
        cwd: workspaceRoot,
        workspaceRoot,
        connectionLabel: row.projectName.trim() || `Cloud ${String(row.cmmdProjectId)}`,
        cmmdProjectId: row.cmmdProjectId,
        defaultModelSelection: null,
        defaultThreadEnvMode: null,
      },
    ];
  });
}

function repoKeyOfCatalogRow(githubRepoFullName: string | null): string | null {
  const raw = githubRepoFullName?.trim();
  if (!raw) return null;
  // CMMD stores a BARE `owner/repo`, which `canonicalRepoKeyOf` rejects on
  // purpose — it demands `host/owner/name` so a repo-less project's
  // `envId\0projectId` fallback can never masquerade as a repository. Qualified
  // with the host here so the two sides compare equal, exactly as the server's
  // `canonicalKeyOfLink` does for the same column.
  const qualified = raw.split("/").filter(Boolean).length === 2 ? `github.com/${raw}` : raw;
  return canonicalRepoKeyOf(qualified);
}
